import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box } from '@mui/material';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children, user, signOut }) => {
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  
  // Use refs for timers to avoid closure issues
  const inactivityTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  
  // 10 minutes in milliseconds
  const INACTIVITY_TIMEOUT = 10 * 60 * 1000;
  // Show warning 1 minute before logout
  const WARNING_TIMEOUT = 9 * 60 * 1000;
  
  console.log('[v2] AuthContext initialized - INACTIVITY_TIMEOUT:', INACTIVITY_TIMEOUT, 'WARNING_TIMEOUT:', WARNING_TIMEOUT);
  window.TIMEOUT_DEBUG = true;

  const handleAutoLogout = useCallback(() => {
    console.log('[handleAutoLogout] Called at', new Date().toLocaleTimeString());
    console.trace('Call stack:');
    
    // Clear any impersonation data
    sessionStorage.removeItem('impersonationToken');
    sessionStorage.removeItem('impersonatedUser'); 
    sessionStorage.removeItem('originalUser');
    sessionStorage.removeItem('isImpersonating');
    
    // Clear user profile
    setUserProfile(null);
    
    // Sign out the user
    signOut();
  }, [signOut]);


  const handleStayLoggedIn = useCallback(() => {
    console.log('[STAY LOGGED IN] Button clicked at', new Date().toLocaleTimeString());
    
    // Clear all existing timers using refs
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    
    // Clear session storage flag and hide warning immediately
    sessionStorage.removeItem('inactivityWarningShown');
    setShowInactivityWarning(false);
    
    // Directly set up new timers instead of relying on event dispatch
    console.log('[STAY LOGGED IN] Setting new warning timer for', WARNING_TIMEOUT / 1000 / 60, 'minutes');
    const warnTimer = setTimeout(() => {
      console.log('[WARNING] Showing inactivity warning at', new Date().toLocaleTimeString());
      sessionStorage.setItem('inactivityWarningShown', 'true');
      setShowInactivityWarning(true);
    }, WARNING_TIMEOUT);
    
    console.log('[STAY LOGGED IN] Setting new logout timer for', INACTIVITY_TIMEOUT / 1000 / 60, 'minutes');
    const logoutTimer = setTimeout(() => {
      console.log('[LOGOUT] Auto logout triggered at', new Date().toLocaleTimeString());
      sessionStorage.removeItem('impersonationToken');
      sessionStorage.removeItem('impersonatedUser'); 
      sessionStorage.removeItem('originalUser');
      sessionStorage.removeItem('isImpersonating');
      sessionStorage.removeItem('inactivityWarningShown');
      setUserProfile(null);
      signOut();
    }, INACTIVITY_TIMEOUT);
    
    warningTimerRef.current = warnTimer;
    inactivityTimerRef.current = logoutTimer;
    
    console.log('[STAY LOGGED IN] Timers reset successfully');
  }, [signOut, WARNING_TIMEOUT, INACTIVITY_TIMEOUT]);

  // Check for Amplify session expiration
  useEffect(() => {
    const checkSession = async () => {
      try {
        const session = await fetchAuthSession({ forceRefresh: false });
        
        if (!session.tokens?.idToken) {
          console.log('[SESSION CHECK] No tokens found - user may be logged out');
          return;
        }
        
        const tokenExpiry = new Date(session.tokens?.idToken?.payload.exp * 1000);
        const now = new Date();
        const minutesUntilExpiry = Math.floor((tokenExpiry - now) / 1000 / 60);
        
        console.log('[SESSION CHECK] at', now.toLocaleTimeString(),
                    '- Token expires at:', tokenExpiry.toLocaleTimeString(), 
                    `(${minutesUntilExpiry} minutes remaining)`);
        
        // Log timer states
        console.log('[SESSION CHECK] Timer states:',
                    'warningTimer:', warningTimerRef.current ? 'SET' : 'NOT SET',
                    'logoutTimer:', inactivityTimerRef.current ? 'SET' : 'NOT SET',
                    'warningShown:', sessionStorage.getItem('inactivityWarningShown'));
        
        // If token is expired or will expire in less than 2 minutes, and we haven't shown warning yet
        if (minutesUntilExpiry < 2 && sessionStorage.getItem('inactivityWarningShown') !== 'true') {
          console.log('[SESSION CHECK] Token expiring soon, but our timer should handle this');
        }
        
        // Check if token is already expired
        if (minutesUntilExpiry < 0) {
          console.log('[SESSION CHECK] Token is EXPIRED!');
        }
      } catch (error) {
        console.log('[SESSION CHECK] Session check error:', error.message || error);
        // Don't force logout here - let our timer handle it
      }
    };
    
    if (user) {
      console.log('[SESSION CHECK] Starting session monitoring for user:', user.username);
      checkSession();
      // Check session every 30 seconds for better visibility
      const interval = setInterval(checkSession, 30000);
      return () => {
        console.log('[SESSION CHECK] Stopping session monitoring');
        clearInterval(interval);
      };
    }
  }, [user]);

  useEffect(() => {
    const loadProfile = async () => {
      console.log('[LOAD PROFILE] Called with user:', user ? user.username : 'NO USER');
      try {
        if (!user) {
          console.log('[LOAD PROFILE] No user - clearing profile');
          setUserProfile(null);
          setLoading(false);
          return;
        }

        const session = await fetchAuthSession({ forceRefresh: false });
        
        // Check if session is valid
        if (!session.tokens?.idToken) {
          console.log('No valid session tokens found');
          setUserProfile(null);
          setLoading(false);
          return;
        }
        
        // Check if currently impersonating
        const isImpersonating = sessionStorage.getItem('isImpersonating') === 'true';
        const impersonatedUser = isImpersonating ? JSON.parse(sessionStorage.getItem('impersonatedUser') || '{}') : null;
        
        if (isImpersonating && impersonatedUser && impersonatedUser.userId) {
          const profile = {
            userId: impersonatedUser.userId,
            email: impersonatedUser.email,
            firstName: impersonatedUser.firstName,
            lastName: impersonatedUser.lastName,
            role: impersonatedUser.role,
            allowedTerminals: impersonatedUser.allowedTerminals || [],
            allowedLocations: impersonatedUser.allowedBusinesses || [],
            // Impersonation now mirrors the target FULLY, including delegated user
            // management: reflect the target's real can_manage_users flag (passed through
            // from admin-impersonate). The user-management Lambda honors the impersonation
            // token, so the page loads scoped to the target's subtree. managerScope is
            // filled live from the getUsers response, so null here is fine.
            canManageUsers: impersonatedUser.canManageUsers === true,
            // Partner-group names passed through from admin-impersonate so the Partner
            // Surcharge menu/page show for impersonated partner users.
            partnerGroups: impersonatedUser.partnerGroups || [],
            managerScope: null,
            isImpersonated: true
          };
          setUserProfile(profile);
          setLoading(false);
          return;
        }
        
        // Get ID token
        const idToken = session.tokens.idToken;
        const idTokenPayload = idToken.payload;
        
        // Parse allowed terminals and locations
        let allowedTerminals = [];
        let allowedLocations = [];
        
        try {
          const terminalsValue = idTokenPayload['custom:allowedTerminals'] || user.attributes?.['custom:allowedTerminals'];
          if (terminalsValue && terminalsValue !== 'all' && terminalsValue !== '*') {
            allowedTerminals = JSON.parse(terminalsValue);
          } else if (terminalsValue === '*') {
            allowedTerminals = '*'; // Admin wildcard
          }
        } catch (e) {
          console.warn('Failed to parse allowedTerminals:', e);
        }

        try {
          const locationsValue = idTokenPayload['custom:allowedBusinesses'] || user.attributes?.['custom:allowedBusinesses'];
          if (locationsValue && locationsValue !== 'all' && locationsValue !== '*') {
            allowedLocations = JSON.parse(locationsValue);
          } else if (locationsValue === '*') {
            allowedLocations = '*'; // Admin wildcard
          }
        } catch (e) {
          console.warn('Failed to parse allowedBusinesses:', e);
        }
        
        const profile = {
          userId: user.username,
          email: idTokenPayload.email || user.attributes?.email,
          firstName: idTokenPayload.given_name || user.attributes?.given_name,
          lastName: idTokenPayload.family_name || user.attributes?.family_name,
          phoneNumber: idTokenPayload.phone_number || user.attributes?.phone_number,
          role: idTokenPayload['custom:role'] || user.attributes?.['custom:role'] || 'viewer',
          allowedTerminals: allowedTerminals,
          allowedLocations: allowedLocations,
          isImpersonated: false
        };

        // Delegated user management: the Can Manage Users flag lives in the DB
        // (users.can_manage_users), not in the JWT. Probe GET /users?limit=1 —
        // 200 ⇒ delegated manager (response carries managerScope for the
        // assignment pickers); 401/403 ⇒ regular user. The probe is NON-BLOCKING
        // (login never waits on it) and only DEFINITIVE outcomes are cached —
        // a transient 5xx/network failure skips the cache so the next load retries.
        profile.canManageUsers = profile.role === 'admin';
        profile.managerScope = null;
        if (profile.role !== 'admin') {
          const cacheKey = `canManageUsers:${profile.userId}`;
          const cached = sessionStorage.getItem(cacheKey);
          let haveCached = false;
          if (cached !== null) {
            try {
              const parsed = JSON.parse(cached);
              if (typeof parsed.canManageUsers === 'boolean') {
                profile.canManageUsers = parsed.canManageUsers;
                profile.managerScope = parsed.managerScope || null;
                haveCached = true;
              }
            } catch (e) {
              // ignore bad cache
            }
          }
          if (!haveCached) {
            const probeUserId = profile.userId;
            const probeToken = idToken.toString();
            (async () => {
              try {
                const apiBaseUrl = process.env.REACT_APP_API_URL;
                const probeResp = await fetch(`${apiBaseUrl}/api/v1/users?limit=1&includeScope=1`, {
                  headers: {
                    'Authorization': `Bearer ${probeToken}`,
                    'Content-Type': 'application/json'
                  }
                });
                if (probeResp.ok) {
                  const probeData = await probeResp.json();
                  const managerScope = probeData.managerScope || null;
                  sessionStorage.setItem(cacheKey, JSON.stringify({ canManageUsers: true, managerScope }));
                  setUserProfile(prev => (prev && prev.userId === probeUserId && !prev.isImpersonated)
                    ? { ...prev, canManageUsers: true, managerScope }
                    : prev);
                } else if (probeResp.status === 401 || probeResp.status === 403) {
                  sessionStorage.setItem(cacheKey, JSON.stringify({ canManageUsers: false, managerScope: null }));
                }
                // other statuses (5xx/504): no cache write — retry on next load
              } catch (probeError) {
                console.warn('canManageUsers probe failed (will retry next load):', probeError.message);
              }
            })();
          }
        }

        // Partner Surcharge dashboard: membership isn't in the JWT either — probe
        // GET /api/v1/businesses?view=partnerSurcharge with a same-day range (cheap,
        // no heavy query) and read back the echoed `group`. 200 ⇒ member of that
        // partner group; 403 ⇒ not a member. Non-blocking, same caching pattern as
        // the canManageUsers probe above.
        profile.partnerGroups = profile.role === 'admin' ? [] : [];   // admin gate handled in hasPermission
        if (profile.role !== 'admin') {
          const pgKey = `partnerGroups:${profile.userId}`;
          const pgCached = sessionStorage.getItem(pgKey);
          let havePg = false;
          if (pgCached !== null) {
            try {
              const p = JSON.parse(pgCached);
              if (Array.isArray(p.partnerGroups)) {
                profile.partnerGroups = p.partnerGroups;
                havePg = true;
              }
            } catch (e) {
              // ignore bad cache
            }
          }
          if (!havePg) {
            const pgUserId = profile.userId;
            const pgToken = idToken.toString();
            (async () => {
              try {
                const apiBaseUrl = process.env.REACT_APP_API_URL;
                const r = await fetch(`${apiBaseUrl}/api/v1/businesses?view=partnerSurcharge&startDate=2026-01-01&endDate=2026-01-01`, {
                  headers: { 'Authorization': `Bearer ${pgToken}`, 'Content-Type': 'application/json' }
                });
                if (r.ok) {
                  const d = await r.json();
                  const groups = d.group ? [d.group] : [];
                  sessionStorage.setItem(pgKey, JSON.stringify({ partnerGroups: groups }));
                  setUserProfile(prev => (prev && prev.userId === pgUserId && !prev.isImpersonated)
                    ? { ...prev, partnerGroups: groups }
                    : prev);
                } else if (r.status === 403) {
                  sessionStorage.setItem(pgKey, JSON.stringify({ partnerGroups: [] }));
                }
                // other statuses (5xx/504): no cache write — retry on next load
              } catch (e) {
                console.warn('partnerGroups probe failed (retry next load):', e.message);
              }
            })();
          }
        }

        setUserProfile(profile);
      } catch (error) {
        console.error('Error loading user profile:', error);
        sessionStorage.removeItem('impersonationToken');
        sessionStorage.removeItem('impersonatedUser');
        sessionStorage.removeItem('originalUser');
        sessionStorage.removeItem('isImpersonating');
        setUserProfile(null);
      } finally {
        setLoading(false);
      }
    };

    if (user || loading) {
      loadProfile();
      
      // Don't set up timers here - they will be set up by the activity listener useEffect
    } else {
      setLoading(false);
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
    }
  }, [user, loading]);

  // Set up activity listeners when user is authenticated  
  useEffect(() => {
    if (!user) return;

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const resetTimer = () => {
      // console.log('[resetTimer] Called at', new Date().toLocaleTimeString());
      
      // Don't reset anything if warning dialog is already shown - let countdown continue
      if (sessionStorage.getItem('inactivityWarningShown') === 'true') {
        // console.log('[resetTimer] Warning shown, not resetting timers');
        return;
      }
      
      // Clear existing timers using refs
      if (inactivityTimerRef.current) {
        // console.log('[resetTimer] Clearing existing inactivity timer');
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      if (warningTimerRef.current) {
        // console.log('[resetTimer] Clearing existing warning timer');
        clearTimeout(warningTimerRef.current);
        warningTimerRef.current = null;
      }
      
      // Start new timers
      // console.log('[resetTimer] Setting warning timer for', WARNING_TIMEOUT / 1000 / 60, 'minutes');
      const warnTimer = setTimeout(() => {
        console.log('[WARNING] Showing inactivity warning at', new Date().toLocaleTimeString());
        sessionStorage.setItem('inactivityWarningShown', 'true');
        setShowInactivityWarning(true);
      }, WARNING_TIMEOUT);
      
      // console.log('[resetTimer] Setting logout timer for', INACTIVITY_TIMEOUT / 1000 / 60, 'minutes');
      const logoutTimer = setTimeout(() => {
        console.log('[LOGOUT] Auto logout triggered at', new Date().toLocaleTimeString());
        sessionStorage.removeItem('impersonationToken');
        sessionStorage.removeItem('impersonatedUser'); 
        sessionStorage.removeItem('originalUser');
        sessionStorage.removeItem('isImpersonating');
        sessionStorage.removeItem('inactivityWarningShown');
        setUserProfile(null);
        signOut();
      }, INACTIVITY_TIMEOUT);
      
      warningTimerRef.current = warnTimer;
      inactivityTimerRef.current = logoutTimer;
    };

    // Add event listeners for user activity
    events.forEach(event => {
      document.addEventListener(event, resetTimer, true);
    });

    // Start initial timers
    console.log('[INIT] Starting initial timers at', new Date().toLocaleTimeString());
    resetTimer();

    // Cleanup event listeners and session storage
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, resetTimer, true);
      });
      sessionStorage.removeItem('inactivityWarningShown');
      // Clear timers on cleanup
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
        warningTimerRef.current = null;
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [user, signOut, INACTIVITY_TIMEOUT, WARNING_TIMEOUT]);

  const hasPermission = (resource, resourceId = null) => {
    if (!userProfile) {
      return false;
    }
    
    // Check resource permission
    
    // Admin has access to everything
    if (userProfile.role === 'admin') {
      return true;
    }
    
    // Special admin-only permissions
    if (['admin_only', 'sftp_management'].includes(resource)) {
      return userProfile.role === 'admin';
    }

    // User Management: admins, or delegated managers (users.can_manage_users
    // flag, discovered via the login probe — see loadProfile)
    if (resource === 'user_management') {
      return userProfile.canManageUsers === true;
    }

    // Partner Surcharge dashboard: admins, or users belonging to a partner group
    // (discovered via the login probe — see loadProfile)
    if (resource === 'partner_surcharge') {
      return userProfile.role === 'admin' || (Array.isArray(userProfile.partnerGroups) && userProfile.partnerGroups.length > 0);
    }

    // Agent has access to all reporting data
    if (userProfile.role === 'agent') {
      return ['overview', 'terminals', 'businesses', 'locations', 'reports', 'upload'].includes(resource);
    }
    
    // SubAgent has access to reporting data and upload
    if (userProfile.role === 'subagent') {
      return ['overview', 'terminals', 'businesses', 'locations', 'reports', 'upload'].includes(resource);
    }
    
    // Merchant has limited access based on allowed terminals/locations
    if (userProfile.role === 'merchant') {
      switch (resource) {
        case 'overview':
          return true; // Can see overview but filtered data
        case 'terminals':
          // Merchants can see terminals if they have either terminal or location permissions
          if (!resourceId) {
            return (userProfile.allowedTerminals && userProfile.allowedTerminals.length > 0) || 
                   (userProfile.allowedLocations && userProfile.allowedLocations.length > 0);
          }
          return userProfile.allowedTerminals && userProfile.allowedTerminals.includes(resourceId);
        case 'businesses':
        case 'locations':
          // Merchants can see locations/businesses if they have location permissions
          if (!resourceId) {
            return userProfile.allowedLocations && userProfile.allowedLocations.length > 0;
          }
          return userProfile.allowedLocations && userProfile.allowedLocations.includes(resourceId);
        case 'reports':
          return (userProfile.allowedTerminals && userProfile.allowedTerminals.length > 0) || 
                 (userProfile.allowedLocations && userProfile.allowedLocations.length > 0);
        default:
          return false;
      }
    }
    
    return false;
  };

  const getAccessToken = async () => {
    try {
      const session = await fetchAuthSession({ forceRefresh: false });
      if (!session.tokens?.accessToken) {
        console.error('No access token in session');
        throw new Error('No access token available');
      }
      return session.tokens.accessToken.toString();
    } catch (error) {
      console.error('Error getting access token:', error);
      throw error;
    }
  };

  const getIdToken = async () => {
    try {
      const session = await fetchAuthSession({ forceRefresh: false });
      if (!session.tokens?.idToken) {
        console.error('No ID token in session');
        throw new Error('No ID token available');
      }
      return session.tokens.idToken.toString();
    } catch (error) {
      console.error('Error getting ID token:', error);
      throw error;
    }
  };

  const refreshProfile = useCallback(() => {
    // Force a re-render by toggling loading state
    setLoading(true);
    // The useEffect will handle the actual profile loading
  }, []);

  const refreshUserProfile = useCallback(async () => {
    // Re-fetch the user profile from Cognito
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      if (!session.tokens?.idToken) {
        console.error('No ID token in session');
        return;
      }
      
      const idTokenPayload = session.tokens.idToken.payload;
      
      let allowedTerminals = [];
      let allowedLocations = [];
      
      try {
        const terminalsValue = idTokenPayload['custom:allowedTerminals'] || user.attributes?.['custom:allowedTerminals'];
        if (terminalsValue && terminalsValue !== 'all') {
          allowedTerminals = JSON.parse(terminalsValue);
        }
      } catch (e) {
        console.warn('Failed to parse allowedTerminals:', e);
      }
      
      try {
        const locationsValue = idTokenPayload['custom:allowedBusinesses'] || user.attributes?.['custom:allowedBusinesses'];
        if (locationsValue && locationsValue !== 'all') {
          allowedLocations = JSON.parse(locationsValue);
        }
      } catch (e) {
        console.warn('Failed to parse allowedBusinesses:', e);
      }
      
      const profile = {
        userId: user.username,
        email: idTokenPayload.email || user.attributes?.email,
        firstName: idTokenPayload.given_name || user.attributes?.given_name,
        lastName: idTokenPayload.family_name || user.attributes?.family_name,
        phoneNumber: idTokenPayload.phone_number || user.attributes?.phone_number,
        role: idTokenPayload['custom:role'] || user.attributes?.['custom:role'] || 'viewer',
        allowedTerminals: allowedTerminals,
        allowedLocations: allowedLocations,
        isImpersonated: false
      };

      // Preserve delegated-management state across refreshes (the probe result
      // is session-cached; omitting this would silently revoke a manager's
      // Users access mid-session)
      profile.canManageUsers = profile.role === 'admin';
      profile.managerScope = null;
      if (profile.role !== 'admin') {
        try {
          const cached = JSON.parse(sessionStorage.getItem(`canManageUsers:${profile.userId}`) || 'null');
          if (cached && typeof cached.canManageUsers === 'boolean') {
            profile.canManageUsers = cached.canManageUsers;
            profile.managerScope = cached.managerScope || null;
          }
        } catch (e) {
          // ignore bad cache
        }
      }

      setUserProfile(profile);
    } catch (error) {
      console.error('Error refreshing user profile:', error);
    }
  }, [user]);

  // Memoize context value to prevent unnecessary re-renders of all consumers
  const value = useMemo(() => ({
    user,
    userProfile,
    loading,
    signOut,
    hasPermission,
    getAccessToken,
    getIdToken,
    refreshProfile,
    refreshUserProfile
  }), [user, userProfile, loading]);

  return (
    <AuthContext.Provider value={value}>
      {children}
      
      {/* Inactivity Warning Dialog */}
      <Dialog 
        open={showInactivityWarning} 
        onClose={() => {}} // Prevent closing by clicking outside
        maxWidth="sm" 
        fullWidth
        disableEscapeKeyDown
        disableBackdropClick
        PaperProps={{
          sx: { backgroundColor: '#fff3cd', border: '1px solid #ffeaa7' }
        }}
      >
        <DialogTitle sx={{ color: '#856404', fontWeight: 'bold' }}>
          Session Timeout Warning
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="body1" sx={{ mb: 2, color: '#856404' }}>
              You will be automatically logged out due to inactivity soon.
            </Typography>
            <Typography variant="body2" sx={{ mt: 2, color: '#856404' }}>
              Click "Stay Logged In" to continue your session.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleStayLoggedIn}
            sx={{ minWidth: 150 }}
          >
            Stay Logged In
          </Button>
          <Button 
            variant="outlined" 
            color="secondary" 
            onClick={handleAutoLogout}
            sx={{ minWidth: 150 }}
          >
            Logout Now
          </Button>
        </DialogActions>
      </Dialog>
    </AuthContext.Provider>
  );
};